import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import apiClient from '../../api/index';
import {
  createDocumentationPost,
  updateDocumentationPost,
  uploadDocumentationAttachment,
  uploadMultipleDocumentationAttachments,
  deleteDocumentationAttachment,
  getDocumentationAttachmentUrl
} from '../../api/documentation';
import { createMarkdownComponents } from '../common/MarkdownComponents';

const DocumentationPostDialog = ({ open, onClose, post = null }) => {
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [route, setRoute] = useState('');
  const [content, setContent] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('single'); // 'single', 'multiple', 'folder'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [orderIndex, setOrderIndex] = useState(0);

  // Detect dark mode
  const isDarkMode = document.documentElement.classList.contains('dark');
  const markdownComponents = createMarkdownComponents(isDarkMode);
  
  // Fetch available roles when dialog opens
  useEffect(() => {
    if (open) {
      // If we're editing an existing post, set initial values
      if (post) {
        setTitle(post.title || '');
        setRoute(post.route || '');
        setContent(post.content || '');
        setSelectedRoles(post.roles?.map(role => role.id) || []);
        setAttachments(post.attachments || []);
        setOrderIndex(post.order_index || 0);
      } else {
        // Reset form for new post
        setTitle('');
        setRoute('');
        setContent('');
        setSelectedRoles([]);
        setAttachments([]);
        setPendingFiles([]);
        setOrderIndex(0);
      }
      
      // Fetch available roles using apiClient
      apiClient.get('/adss/v1/users/roles')
        .then(response => {
          setAvailableRoles(response.data);
        })
        .catch(err => {
          console.error('Error fetching roles:', err);
          setError('Failed to load roles');
        });
    }
  }, [open, post]);
  
  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };
  
  const handleRoleChange = (roleId) => {
    if (selectedRoles.includes(roleId)) {
      setSelectedRoles(selectedRoles.filter(id => id !== roleId));
    } else {
      setSelectedRoles([...selectedRoles, roleId]);
    }
  };
  
  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!route.trim()) {
      setError('Route is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const postData = {
        title: title.trim(),
        route: route.trim(),
        content,
        role_ids: selectedRoles,
        order_index: orderIndex
      };
      
      let savedPost;
      
      if (post) {
        // Update existing post
        savedPost = await updateDocumentationPost(post.id, postData);
      } else {
        // Create new post
        savedPost = await createDocumentationPost(postData);
        
        // Upload pending files if any
        if (pendingFiles.length > 0) {
          let updatedContent = content;
          
          for (const file of pendingFiles) {
            try {
              await uploadDocumentationAttachment(savedPost.id, file);
              
              // Update the content to replace placeholder URLs with real ones
              const realUrl = getDocumentationAttachmentUrl(savedPost.id, file.name);
              updatedContent = updatedContent.replace(
                `](will-be-uploaded)`,
                `](${realUrl})`
              );
            } catch (uploadErr) {
              console.error('Error uploading file:', uploadErr);
              // Continue with other files even if one fails
            }
          }
          
          // Update the post content with real URLs if any files were uploaded
          if (updatedContent !== content) {
            try {
              await updateDocumentationPost(savedPost.id, {
                ...postData,
                content: updatedContent
              });
            } catch (updateErr) {
              console.error('Error updating post content with file URLs:', updateErr);
            }
          }
        }
      }
      
      onClose(true); // Refresh parent component
    } catch (err) {
      console.error('Error saving documentation post:', err);
      setError('Failed to save documentation post');
    } finally {
      setLoading(false);
    }
  };
  
  const generateMarkdownReference = (file, fileUrl = null) => {
    const url = fileUrl || 'will-be-uploaded';
    let markdownReference = '';
    
    if (file.type.startsWith('image/')) {
      markdownReference = `![${file.name}](${url})`;
    } else if (file.type === 'application/pdf') {
      markdownReference = `📄 [${file.name}](${url})`;
    } else if (file.name.endsWith('.zip')) {
      markdownReference = `📦 [${file.name}](${url})`;
    } else if (file.name.endsWith('.csv')) {
      markdownReference = `📊 [${file.name}](${url})`;
    } else {
      markdownReference = `📎 [${file.name}](${url})`;
    }
    
    // Add a comment about sizing
    if (file.type.startsWith('image/')) {
      markdownReference += '\n<!-- To resize: ![filename|300](url) or ![filename|50%](url) -->';
    } else if (file.type === 'application/pdf') {
      markdownReference += '\n<!-- To resize: 📄 [filename|300](url) or 📄 [filename|50%](url) -->';
    }
    
    return markdownReference;
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    console.log('Files selected:', files.map(f => f.name), 'Post exists:', !!post);
    
    setUploadProgress(true);
    
    try {
      if (post) {
        // Existing post - upload immediately
        console.log('Uploading files to existing post:', post.id);
        let uploadedAttachments;
        
        if (files.length === 1) {
          // Single file upload
          uploadedAttachments = [await uploadDocumentationAttachment(post.id, files[0])];
        } else {
          // Multiple files upload using bulk endpoint
          uploadedAttachments = await uploadMultipleDocumentationAttachments(post.id, files);
        }
        
        console.log('Files uploaded successfully:', uploadedAttachments);
        
        setAttachments(prev => [...prev, ...uploadedAttachments]);
        
        // Add markdown references for all files
        const markdownReferences = files.map(file => {
          const fileUrl = getDocumentationAttachmentUrl(post.id, file.name);
          return generateMarkdownReference(file, fileUrl);
        });
        
        setContent(prevContent => prevContent + '\n\n' + markdownReferences.join('\n\n'));
      } else {
        // New post - store files for later upload
        console.log('Adding files to pending list for new post');
        setPendingFiles(prev => [...prev, ...files]);
        
        // Add placeholder markdown references
        const markdownReferences = files.map(file => generateMarkdownReference(file));
        
        setContent(prevContent => prevContent + '\n\n' + markdownReferences.join('\n\n'));
      }
    } catch (err) {
      console.error('Error handling files:', err);
      setError(`Failed to handle files: ${err.message}`);
    } finally {
      setUploadProgress(false);
      // Reset the file input so the same files can be selected again
      event.target.value = '';
    }
  };
  
  const handleDeleteAttachment = async (attachmentId) => {
    if (!post) return;
    
    try {
      await deleteDocumentationAttachment(post.id, attachmentId);
      setAttachments(attachments.filter(att => att.id !== attachmentId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment');
    }
  };
  
  const insertMarkdownSyntax = (syntax, placeholder = '') => {
    const textArea = document.getElementById('markdown-editor');
    if (!textArea) return;
    
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selectedText = content.substring(start, end) || placeholder;
    
    const beforeText = content.substring(0, start);
    const afterText = content.substring(end);
    
    let newText;
    switch (syntax) {
      case 'bold':
        newText = `${beforeText}**${selectedText}**${afterText}`;
        break;
      case 'italic':
        newText = `${beforeText}_${selectedText}_${afterText}`;
        break;
      case 'link':
        newText = `${beforeText}[${selectedText}](url)${afterText}`;
        break;
      case 'image':
        newText = `${beforeText}![${selectedText}](image-url)${afterText}`;
        break;
      case 'list':
        newText = `${beforeText}\n- ${selectedText}\n- Item 2\n- Item 3${afterText}`;
        break;
      case 'code':
        newText = `${beforeText}\`\`\`\n${selectedText}\n\`\`\`${afterText}`;
        break;
      default:
        newText = content;
    }
    
    setContent(newText);
    
    // Re-focus the textarea and set cursor position
    setTimeout(() => {
      textArea.focus();
      const newCursorPosition = syntax === 'link' 
        ? start + selectedText.length + 3 // Position cursor at the "url" part
        : start + newText.length - afterText.length;
      
      textArea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };
  
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="documentation-dialog" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" aria-hidden="true" onClick={() => onClose(false)}></div>
        
        {/* Dialog Panel */}
        <div className="relative bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-xl w-full max-w-4xl mx-auto p-6 text-left">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              {post ? 'Edit Documentation' : 'Create New Documentation'}
            </h3>
            <button 
              onClick={() => onClose(false)} 
              className="p-1 rounded-md text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          {/* Dialog Content */}
          <div className="mt-2 space-y-4">
            {error && (
              <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              {/* Title Input */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-1">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
                  placeholder="Documentation title"
                />
              </div>
              
              {/* Route Input */}
              <div>
                <label htmlFor="route" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-1">
                  Route Name
                </label>
                <input
                  type="text"
                  id="route"
                  value={route}
                  onChange={(e) => setRoute(e.target.value)}
                  className="w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
                  placeholder="URL-friendly route name (e.g., 'getting-started', 'api-guide')"
                />
                <p className="mt-1 text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  This will be used in the URL: /documentation/{route}
                </p>
              </div>
              
              {/* Order Index */}
              <div>
                <label htmlFor="order-index" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-1">
                  Order Index
                </label>
                <input
                  type="number"
                  id="order-index"
                  value={orderIndex}
                  onChange={(e) => setOrderIndex(parseInt(e.target.value) || 0)}
                  className="w-40 p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
                />
                <p className="mt-1 text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  Lower numbers appear first
                </p>
              </div>
              
              {/* Roles Selection */}
              <div>
                <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-1">
                  Visible to Roles
                </label>
                <div className="p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background min-h-10">
                  <div className="flex flex-wrap gap-2">
                    {selectedRoles.length > 0 ? (
                      selectedRoles.map((roleId) => {
                        const role = availableRoles.find(r => r.id === roleId);
                        return role ? (
                          <div 
                            key={roleId} 
                            className="px-2 py-1 text-xs rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 flex items-center"
                          >
                            <span>{role.name}</span>
                            <button 
                              className="ml-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                              onClick={() => handleRoleChange(roleId)}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                              </svg>
                            </button>
                          </div>
                        ) : null;
                      })
                    ) : (
                      <span className="text-ui-light-text-muted dark:text-ui-dark-text-muted text-sm py-1">No roles selected</span>
                    )}
                  </div>
                </div>
                <div className="mt-2 max-h-32 overflow-y-auto">
                  <div className="space-y-1">
                    {availableRoles.map((role) => (
                      <button
                        key={role.id}
                        onClick={() => handleRoleChange(role.id)}
                        className={`px-3 py-1.5 text-sm w-full text-left rounded-md transition-colors ${
                          selectedRoles.includes(role.id)
                            ? 'bg-primary-50 dark:bg-primary-900/10 text-primary-700 dark:text-primary-400'
                            : 'hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'
                        }`}
                      >
                        {role.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Content Editor */}
              <div>
                <div className="border border-ui-light-border dark:border-ui-dark-border rounded-md overflow-hidden">
                  {/* Tabs */}
                  <div className="flex border-b border-ui-light-border dark:border-ui-dark-border">
                    <button
                      onClick={() => handleTabChange(0)}
                      className={`px-4 py-2 text-sm font-medium ${
                        tabValue === 0
                          ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                          : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'
                      }`}
                    >
                      Write
                    </button>
                    <button
                      onClick={() => handleTabChange(1)}
                      className={`px-4 py-2 text-sm font-medium ${
                        tabValue === 1
                          ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                          : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'
                      }`}
                    >
                      Preview
                    </button>
                  </div>
                  
                  {/* Editor Toolbar */}
                  <div className={`${tabValue === 0 ? 'block' : 'hidden'}`}>
                    <div className="flex flex-wrap p-2 gap-1 bg-ui-light-background dark:bg-ui-dark-background border-b border-ui-light-border dark:border-ui-dark-border">
                      <button
                        onClick={() => insertMarkdownSyntax('bold', 'bold text')}
                        className="p-1.5 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                        title="Bold"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 12h8a4 4 0 100-8H6v8zm0 0h10a4 4 0 110 8H6v-8z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => insertMarkdownSyntax('italic', 'italic text')}
                        className="p-1.5 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                        title="Italic"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 5H8a1 1 0 00-1 1v12a1 1 0 001 1h2m8-10h-6m4 6h-2"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => insertMarkdownSyntax('list')}
                        className="p-1.5 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                        title="List"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => insertMarkdownSyntax('link', 'link text')}
                        className="p-1.5 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                        title="Link"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => insertMarkdownSyntax('image', 'alt text')}
                        className="p-1.5 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                        title="Image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => insertMarkdownSyntax('code', 'code here')}
                        className="p-1.5 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                        title="Code Block"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                      
                      <div className="ml-auto flex items-center space-x-2">
                        {/* Upload Mode Selector */}
                        <select
                          value={uploadMode}
                          onChange={(e) => setUploadMode(e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                          disabled={uploadProgress}
                        >
                          <option value="single">Single File</option>
                          <option value="multiple">Multiple Files</option>
                          <option value="folder">Folder (Chrome/Edge)</option>
                        </select>
                        
                        {/* File Inputs */}
                        <input
                          accept=".pdf,.png,.jpeg,.jpg,.zip,.csv"
                          className="hidden"
                          id="file-upload-single"
                          type="file"
                          onChange={handleFileUpload}
                          disabled={uploadProgress}
                        />
                        <input
                          accept=".pdf,.png,.jpeg,.jpg,.zip,.csv"
                          className="hidden"
                          id="file-upload-multiple"
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          disabled={uploadProgress}
                        />
                        <input
                          className="hidden"
                          id="file-upload-folder"
                          type="file"
                          webkitdirectory
                          directory=""
                          mozdirectory=""
                          multiple
                          onChange={handleFileUpload}
                          disabled={uploadProgress}
                        />
                        
                        <label 
                          htmlFor={`file-upload-${uploadMode}`}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/10 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/20 transition-colors ${
                            uploadProgress ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                          }`}
                        >
                          {uploadProgress ? (
                            <svg className="w-4 h-4 text-primary-600 dark:text-primary-400 animate-spin mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                            </svg>
                          )}
                          {uploadMode === 'single' ? 'Attach File' : uploadMode === 'multiple' ? 'Attach Files' : 'Attach Folder'}
                        </label>
                        
                        {/* Browser compatibility notice */}
                        {uploadMode === 'folder' && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') 
                              ? 'Safari: Use "Multiple Files" and select all files manually'
                              : 'Click to select entire folder'}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Text Editor */}
                    <textarea
                      id="markdown-editor"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      className="w-full p-4 bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none font-mono text-sm"
                      rows="12"
                      placeholder="Write markdown content here..."
                    ></textarea>
                  </div>
                  
                  {/* Preview */}
                  <div
                    className={`${tabValue === 1 ? 'block' : 'hidden'} p-4 min-h-64 max-h-[500px] bg-ui-light-background dark:bg-ui-dark-background overflow-auto`}
                  >
                    {content ? (
                      <div className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none">
                        <ReactMarkdown
                          components={markdownComponents}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeSlug, rehypeRaw]}
                        >
                          {content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="text-ui-light-text-muted dark:text-ui-dark-text-muted italic">
                        Preview will appear here
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Pending Files for New Posts */}
              {!post && pendingFiles.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                    Files to Upload
                  </h4>
                  
                  <div className="border border-ui-light-border dark:border-ui-dark-border rounded-md p-3 bg-ui-light-background dark:bg-ui-dark-background">
                    <div className="space-y-2">
                      {pendingFiles.map((file, index) => {
                        const getFileTypeIcon = (filename) => {
                          if (filename.match(/\.(png|jpg|jpeg|gif|webp)$/i)) return '🖼️';
                          if (filename.endsWith('.pdf')) return '📄';
                          if (filename.endsWith('.zip')) return '📦';
                          if (filename.endsWith('.csv')) return '📊';
                          return '📎';
                        };
                        
                        return (
                          <div 
                            key={index} 
                            className="flex items-center justify-between py-2 border-b border-ui-light-border dark:border-ui-dark-border last:border-0"
                          >
                            <div className="flex items-center text-ui-light-text-secondary dark:text-ui-dark-text-tertiary min-w-0 flex-1">
                              <span className="text-lg mr-2">{getFileTypeIcon(file.name)}</span>
                              <span className="text-sm truncate">{file.name}</span>
                              <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted ml-2">(will upload after save)</span>
                            </div>
                            
                            <button
                              onClick={() => {
                                const newPendingFiles = [...pendingFiles];
                                newPendingFiles.splice(index, 1);
                                setPendingFiles(newPendingFiles);
                              }}
                              className="p-1 text-status-error-light-text dark:text-status-error-dark-text hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover rounded-md"
                              title="Remove"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Attached Files */}
              {post && attachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                    Attached Files
                  </h4>
                  
                  <div className="border border-ui-light-border dark:border-ui-dark-border rounded-md p-3 bg-ui-light-background dark:bg-ui-dark-background">
                    <div className="space-y-2">
                      {attachments.map(attachment => {
                        const fileUrl = getDocumentationAttachmentUrl(post.id, attachment.filename);
                        const getFileTypeIcon = (filename) => {
                          if (filename.match(/\.(png|jpg|jpeg|gif|webp)$/i)) return '🖼️';
                          if (filename.endsWith('.pdf')) return '📄';
                          if (filename.endsWith('.zip')) return '📦';
                          if (filename.endsWith('.csv')) return '📊';
                          return '📎';
                        };
                        
                        const getMarkdownReference = (filename) => {
                          if (filename.match(/\.(png|jpg|jpeg|gif|webp)$/i)) {
                            return `![${filename}](${fileUrl})`;
                          } else if (filename.endsWith('.pdf')) {
                            return `📄 [${filename}](${fileUrl})`;
                          } else if (filename.endsWith('.zip')) {
                            return `📦 [${filename}](${fileUrl})`;
                          } else if (filename.endsWith('.csv')) {
                            return `📊 [${filename}](${fileUrl})`;
                          } else {
                            return `📎 [${filename}](${fileUrl})`;
                          }
                        };
                        
                        return (
                          <div 
                            key={attachment.id} 
                            className="flex items-center justify-between py-2 border-b border-ui-light-border dark:border-ui-dark-border last:border-0"
                          >
                            <div className="flex items-center text-ui-light-text-secondary dark:text-ui-dark-text-tertiary min-w-0 flex-1">
                              <span className="text-lg mr-2">{getFileTypeIcon(attachment.filename)}</span>
                              <span className="text-sm truncate">{attachment.filename}</span>
                            </div>
                            
                            <div className="flex items-center gap-1 ml-2">
                              <button
                                onClick={() => {
                                  const markdown = getMarkdownReference(attachment.filename);
                                  navigator.clipboard.writeText(markdown);
                                }}
                                className="p-1 text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover rounded-md"
                                title="Copy markdown reference"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                </svg>
                              </button>
                              
                              <button
                                onClick={() => handleDeleteAttachment(attachment.id)}
                                className="p-1 text-status-error-light-text dark:text-status-error-dark-text hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover rounded-md"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Dialog Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
            >
              Cancel
            </button>
            
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 text-white dark:text-ui-dark-text-primary animate-spin mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                post ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPostDialog;